import * as React from 'react';
import { StyleSheet, View, Text, Image, TouchableOpacity } from 'react-native';
import { font } from '../../style/text';
import color from '../../style/color';
import { getLocalizedTexts } from '../../Locales';
import { Region, getRegion } from '../../lib/auth/region';
import type { OrderData } from './giftCardPaymentModel';
import type { GiftCardGranularConditionsOfUse } from '../../api/graphql/queries/giftCardPaymentSessionOrderData';

export { OrderData };

const applePayLogo = { uri: '/assets/images/logos/apple-pay.svg' };
const googlePayLogo = { uri: '/assets/images/logos/google-pay.svg' };
const creditCardLogo = { uri: '/assets/images/icons/credit-card-simple.svg' };
const checkmarkRadioIcon = { uri: '/assets/images/icons/checkmark-radio.svg' };

const defaultConditionIcon = { uri: '/assets/images/icons/info-outlined.svg' };

function getConditionIcon(type: string, shouldBeCrossedOut?: boolean): { uri: string } {
    const region = getRegion();
    switch (type) {
        case 'validityPeriod':
        case 'unlimitedValidity':
            return { uri: '/assets/images/icons/hourglass.svg' };
        case 'activationDelay':
            return { uri: '/assets/images/icons/timer.svg' };
        case 'offlineUsageModality':
            return shouldBeCrossedOut
                ? { uri: '/assets/images/icons/merchant-cross.svg' }
                : { uri: '/assets/images/icons/merchant.svg' };
        case 'onlineUsageModality':
            return shouldBeCrossedOut
                ? { uri: '/assets/images/icons/globe-cross.svg' }
                : { uri: '/assets/images/icons/globe.svg' };
        case 'conjunctionWithOnlineCashback':
            if (region === Region.US) {
                return shouldBeCrossedOut
                    ? { uri: '/assets/images/icons/dollar-square-slash.svg' }
                    : { uri: '/assets/images/icons/dollar-circle.svg' };
            }
            return shouldBeCrossedOut
                ? { uri: '/assets/images/icons/euro-square-slash.svg' }
                : { uri: '/assets/images/icons/euro-circle.svg' };
        case 'multipleUses':
            return shouldBeCrossedOut
                ? { uri: '/assets/images/icons/cycle-cross.svg' }
                : { uri: '/assets/images/icons/cycle-check.svg' };
        case 'conjunctionWithOtherCashbackGiftCards':
            return shouldBeCrossedOut
                ? { uri: '/assets/images/icons/cards-cross.svg' }
                : { uri: '/assets/images/icons/cards.svg' };
        case 'conjunctionWithOtherNonCashbackGiftCardPaymentMethods':
            return shouldBeCrossedOut
                ? { uri: '/assets/images/icons/credit-card-cross.svg' }
                : { uri: '/assets/images/icons/credit-cards.svg' };
        case 'refundConditionOverride':
            return { uri: '/assets/images/icons/refresh-thick.svg' };
        case 'notRefundableOrExchangeable':
            return { uri: '/assets/images/icons/cross-circle-outlined.svg' };
        case 'catchAll':
        default:
            return defaultConditionIcon;
    }
}

export const PAYMENT_METHOD_LOGOS = { applePayLogo, googlePayLogo, creditCardLogo };

export function WalletMethodRow({
    logo,
    label,
    selected,
    onPress,
    hasBorderBottom = false,
}: {
    logo: { uri: string };
    label: string;
    selected: boolean;
    onPress: () => void;
    hasBorderBottom?: boolean;
}) {
    return (
        <TouchableOpacity
            style={[styles.walletMethodRow, hasBorderBottom && styles.walletMethodRowBorder]}
            onPress={onPress}
            activeOpacity={0.7}
        >
            <View style={styles.walletMethodLogoContainer}>
                <Image source={logo} style={styles.walletMethodLogo} />
            </View>
            <Text style={styles.walletMethodLabel}>{label}</Text>
            <View style={[styles.walletMethodRadio, selected && styles.walletMethodRadioSelected]}>
                {selected && <Image source={checkmarkRadioIcon} style={styles.walletMethodRadioCheckmark} />}
            </View>
        </TouchableOpacity>
    );
}

function ConditionText({
    text,
    shouldBeCrossedOut,
    hasTitle,
}: {
    text: string;
    shouldBeCrossedOut?: boolean | null;
    hasTitle: boolean;
}) {
    const unescaped = text.replace(/\\"/g, '"').replace(/\\n/g, '\n');
    const crossedOutStyle = shouldBeCrossedOut ? styles.conditionTextCrossedOut : undefined;
    const textStyle = [hasTitle ? styles.conditionSubtext : styles.conditionText, crossedOutStyle];
    const match = unescaped.match(/\[([^\]]+)\]\(([^)]+)\)/);
    if (!match) {
        return <Text style={textStyle}>{unescaped}</Text>;
    }
    const [fullMatch, linkText, linkUrl] = match;
    const isValidUrl = linkUrl.startsWith('https://') || linkUrl.startsWith('http://');
    const idx = unescaped.indexOf(fullMatch);
    const before = unescaped.slice(0, idx);
    const after = unescaped.slice(idx + fullMatch.length);
    return (
        <Text style={textStyle}>
            {before}
            <Text
                style={[textStyle, isValidUrl && styles.conditionLink]}
                onPress={isValidUrl ? () => window.open(linkUrl, '_blank') : undefined}
            >
                {linkText}
            </Text>
            {after}
        </Text>
    );
}

type GranularConditionEntry = {
    type: string;
    title: string;
    text?: string;
    shouldBeCrossedOut?: boolean;
};

function splitGranularConditionsOfUse(
    granularConditionsOfUse: GiftCardGranularConditionsOfUse,
    texts: ReturnType<typeof getLocalizedTexts>['giftCardPaymentPage']['granularConditionsOfUse']
): { primary: GranularConditionEntry[]; secondary: GranularConditionEntry[] } {
    const {
        multipleUses,
        conjunctionWithOtherNonCashbackGiftCardPaymentMethods,
        conjunctionWithOtherCashbackGiftCards,
        validityPeriodText,
        activationDelayText,
        catchAll,
        shouldHighlightCatchAllConditions,
        refundConditionOverrideText,
        conjunctionWithOnlineCashback,
        offlineUsageModalityText,
        onlineUsageModalityText,
        notRefundableOrExchangeableText,
    } = granularConditionsOfUse;

    const primary = ([
        multipleUses?.description || multipleUses?.shouldBeCrossedOut
            ? { type: 'multipleUses', title: texts.multipleUses, text: multipleUses.description ?? undefined, shouldBeCrossedOut: !!multipleUses.shouldBeCrossedOut }
            : undefined,
        conjunctionWithOtherNonCashbackGiftCardPaymentMethods?.description || conjunctionWithOtherNonCashbackGiftCardPaymentMethods?.shouldBeCrossedOut
            ? { type: 'conjunctionWithOtherNonCashbackGiftCardPaymentMethods', title: texts.conjunctionWithOtherNonCashbackGiftCardPaymentMethods, text: conjunctionWithOtherNonCashbackGiftCardPaymentMethods.description ?? undefined, shouldBeCrossedOut: !!conjunctionWithOtherNonCashbackGiftCardPaymentMethods.shouldBeCrossedOut }
            : undefined,
        conjunctionWithOtherCashbackGiftCards?.description || conjunctionWithOtherCashbackGiftCards?.shouldBeCrossedOut
            ? { type: 'conjunctionWithOtherCashbackGiftCards', title: texts.conjunctionWithOtherCashbackGiftCards, text: conjunctionWithOtherCashbackGiftCards.description ?? undefined, shouldBeCrossedOut: !!conjunctionWithOtherCashbackGiftCards.shouldBeCrossedOut }
            : undefined,
        validityPeriodText
            ? { type: 'validityPeriod', title: validityPeriodText }
            : undefined,
        activationDelayText
            ? { type: 'activationDelay', title: activationDelayText }
            : undefined,
        shouldHighlightCatchAllConditions && catchAll?.description
            ? { type: 'catchAll', title: texts.catchAll, text: catchAll.description }
            : undefined,
        refundConditionOverrideText
            ? { type: 'refundConditionOverride', title: refundConditionOverrideText, text: notRefundableOrExchangeableText ?? undefined }
            : { type: 'notRefundableOrExchangeable', title: texts.notRefundableOrExchangeable, text: notRefundableOrExchangeableText ?? undefined },
    ] as (GranularConditionEntry | undefined)[]).filter((c): c is GranularConditionEntry => !!c);

    const secondary = ([
        { type: 'offlineUsageModality', title: texts.offlineUsageModality, text: offlineUsageModalityText ?? undefined },
        onlineUsageModalityText
            ? { type: 'onlineUsageModality', title: texts.onlineUsageModality, text: onlineUsageModalityText }
            : undefined,
        conjunctionWithOnlineCashback
            ? { type: 'conjunctionWithOnlineCashback', title: texts.conjunctionWithOnlineCashback, text: conjunctionWithOnlineCashback.description ?? undefined, shouldBeCrossedOut: !!conjunctionWithOnlineCashback.shouldBeCrossedOut }
            : undefined,
        !shouldHighlightCatchAllConditions && catchAll?.description
            ? { type: 'catchAll', title: texts.catchAll, text: catchAll.description }
            : undefined,
        !(multipleUses?.description || multipleUses?.shouldBeCrossedOut) && multipleUses
            ? { type: 'multipleUses', title: texts.multipleUses }
            : undefined,
        !(conjunctionWithOtherCashbackGiftCards?.description || conjunctionWithOtherCashbackGiftCards?.shouldBeCrossedOut) && conjunctionWithOtherCashbackGiftCards
            ? { type: 'conjunctionWithOtherCashbackGiftCards', title: texts.conjunctionWithOtherCashbackGiftCards }
            : undefined,
        !(conjunctionWithOtherNonCashbackGiftCardPaymentMethods?.description || conjunctionWithOtherNonCashbackGiftCardPaymentMethods?.shouldBeCrossedOut) && conjunctionWithOtherNonCashbackGiftCardPaymentMethods
            ? { type: 'conjunctionWithOtherNonCashbackGiftCardPaymentMethods', title: texts.conjunctionWithOtherNonCashbackGiftCardPaymentMethods }
            : undefined,
        !validityPeriodText
            ? { type: 'unlimitedValidity', title: texts.unlimitedValidity }
            : undefined,
    ] as (GranularConditionEntry | undefined)[]).filter((c): c is GranularConditionEntry => !!c);

    return { primary, secondary };
}

export function GranularConditionsBlock({
    granularConditionsOfUse,
    conditionsOfUseUrl,
}: {
    granularConditionsOfUse: GiftCardGranularConditionsOfUse;
    conditionsOfUseUrl?: string;
}) {
    const texts = getLocalizedTexts().giftCardPaymentPage;
    const condTexts = texts.granularConditionsOfUse;
    const [isExpanded, setIsExpanded] = React.useState(false);
    const { primary, secondary } = splitGranularConditionsOfUse(granularConditionsOfUse, condTexts);
    const hasSecondary = secondary.length > 0;
    const allConditions = isExpanded ? [...primary, ...secondary] : primary;
    return (
        <View style={styles.conditionsBlock}>
            <Text style={styles.conditionsBlockTitle}>{texts.importantConditions}</Text>
            {allConditions.map((condition, index) => (
                <View
                    key={condition.type}
                    style={[
                        styles.conditionRow,
                        index < allConditions.length - 1 && styles.conditionRowBorder,
                    ]}
                >
                    <Image source={getConditionIcon(condition.type, condition.shouldBeCrossedOut)} style={styles.conditionIcon} />
                    <View style={styles.conditionContent}>
                        <Text
                            style={[
                                styles.conditionTitle,
                                condition.shouldBeCrossedOut && styles.conditionTextCrossedOut,
                            ]}
                        >
                            {condition.title}
                        </Text>
                        {condition.text ? (
                            <ConditionText
                                text={condition.text}
                                shouldBeCrossedOut={condition.shouldBeCrossedOut}
                                hasTitle={true}
                            />
                        ) : null}
                    </View>
                </View>
            ))}
            {hasSecondary && (
                <TouchableOpacity
                    style={styles.seeAllConditionsButton}
                    onPress={() => setIsExpanded(!isExpanded)}
                    activeOpacity={0.7}
                >
                    <Text style={styles.seeAllConditionsText}>
                        {isExpanded ? texts.seeLessConditions : texts.seeAllConditions}
                    </Text>
                </TouchableOpacity>
            )}
            {conditionsOfUseUrl && (
                <TouchableOpacity
                    style={styles.seeAllConditionsButton}
                    onPress={() => window.open(conditionsOfUseUrl, '_blank')}
                    activeOpacity={0.7}
                >
                    <Text style={styles.seeAllConditionsText}>{texts.seeAllConditions}</Text>
                </TouchableOpacity>
            )}
        </View>
    );
}

const BRAND_LOGO_HEIGHT = 32;

function useImageAspectRatio(url: string | undefined): number {
    const [aspectRatio, setAspectRatio] = React.useState(1);
    React.useEffect(() => {
        if (url) Image.getSize(url, (width, height) => { if (height > 0) setAspectRatio(width / height); });
    }, [url]);
    return aspectRatio;
}

export function GiftCardPreview({ orderData, numberOfCards }: { orderData: OrderData; numberOfCards: number }) {
    const texts = getLocalizedTexts().giftCardPaymentPage;
    const bgColor = orderData.cardBackgroundColor ?? color.black;
    const textColor = orderData.cardTextColor ?? color.white;
    const logoAspectRatio = useImageAspectRatio(orderData.brandLogoUrl ?? undefined);
    return (
        <View style={[styles.giftCard, { backgroundColor: bgColor }]}>
            <View style={styles.giftCardTop}>
                {orderData.brandLogoUrl ? (
                    <Image
                        source={{ uri: orderData.brandLogoUrl }}
                        style={[styles.giftCardBrandLogo, { width: BRAND_LOGO_HEIGHT * logoAspectRatio }]}
                    />
                ) : (
                    <Text style={[styles.giftCardBrandName, { color: textColor }]}>
                        {orderData.brandName}
                    </Text>
                )}
                {numberOfCards > 1 && (
                    <View style={styles.giftCardBadge}>
                        <Text style={styles.giftCardBadgeText}>{numberOfCards}</Text>
                    </View>
                )}
            </View>
            <View style={styles.giftCardCenter}>
                <Text style={[styles.giftCardAmount, { color: textColor }]}>
                    {orderData.totalAmountFormatted}
                </Text>
                {orderData.cashbackAmountFormatted && (
                    <View style={styles.cashbackBadge}>
                        <Text style={styles.cashbackBadgeAmount}>
                            {orderData.cashbackAmountFormatted}
                        </Text>
                        <Text style={styles.cashbackBadgeLabel}>{texts.immediateCashback}</Text>
                    </View>
                )}
            </View>
        </View>
    );
}

export function OrderSummary({ orderData }: { orderData: OrderData }) {
    const texts = getLocalizedTexts().giftCardPaymentPage;
    return (
        <View style={styles.orderCard}>
            <Text style={styles.orderTitle}>{texts.yourOrder}</Text>
            {orderData.items.map((item, index) => (
                <View key={index} style={styles.orderRow}>
                    <Text style={styles.orderItemLabel}>{item.label}</Text>
                    <Text style={styles.orderItemQty}>{item.quantity}x</Text>
                </View>
            ))}
            <View style={[styles.orderRow, styles.orderRowBorderTop]}>
                <Text style={styles.orderTotalLabel}>{texts.totalAmount}</Text>
                <Text style={styles.orderTotalValue}>{orderData.totalAmountFormatted}</Text>
            </View>

            {orderData.cashbackAmountFormatted && (
                <>
                    <Text style={[styles.orderTitle, styles.orderTitleBorderTop]}>{texts.yourCashback}</Text>
                    <Text style={styles.cashbackDescription}>
                        {orderData.cashbackDescription ?? texts.defaultCashbackDescription}
                    </Text>
                    <View style={styles.orderRow}>
                        <Text style={styles.orderItemLabel}>{texts.totalCashback}</Text>
                        <Text style={styles.orderTotalValue}>{orderData.cashbackAmountFormatted}</Text>
                    </View>
                </>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    walletMethodRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 14,
        backgroundColor: color.white,
        gap: 12,
    },
    walletMethodRowBorder: {
        borderBottomWidth: 1,
        borderBottomColor: '#E3E3E3',
    },
    walletMethodLogoContainer: {
        width: 48,
        height: 32,
        alignItems: 'center',
        justifyContent: 'center',
    },
    walletMethodLogo: {
        width: 48,
        height: 24,
        resizeMode: 'contain',
    },
    walletMethodLabel: {
        flex: 1,
        fontFamily: font.instrumentSansSemiBold,
        fontSize: 16,
        lineHeight: 20,
        color: color.black,
    },
    walletMethodRadio: {
        width: 24,
        height: 24,
        borderRadius: 12,
        borderWidth: 1.5,
        borderColor: '#C4C4C4',
        alignItems: 'center',
        justifyContent: 'center',
    },
    walletMethodRadioSelected: {
        backgroundColor: color.black,
        borderColor: color.black,
    },
    walletMethodRadioCheckmark: {
        width: 10,
        height: 8,
    },

    conditionsBlock: {
        width: '100%',
        borderWidth: 1,
        borderColor: color.black,
        borderRadius: 16,
        marginBottom: 8,
        overflow: 'hidden',
    },
    conditionsBlockTitle: {
        fontFamily: font.instrumentSansBold,
        fontSize: 18,
        lineHeight: 24,
        color: color.black,
        paddingHorizontal: 16,
        paddingTop: 16,
        paddingBottom: 8,
    },
    conditionRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 12,
        paddingHorizontal: 16,
        paddingVertical: 12,
    },
    conditionContent: {
        flex: 1,
        gap: 2,
    },
    conditionTitle: {
        fontFamily: font.instrumentSansRegular,
        fontSize: 14,
        lineHeight: 20,
        color: color.black,
    },
    conditionRowBorder: {
        borderBottomWidth: 1,
        borderBottomColor: '#E3E3E3',
    },
    conditionIcon: {
        width: 20,
        height: 20,
        resizeMode: 'contain',
        flexShrink: 0,
    },
    conditionText: {
        fontFamily: font.instrumentSansRegular,
        fontSize: 14,
        lineHeight: 20,
        color: color.black,
        flex: 1,
    },
    conditionSubtext: {
        fontFamily: font.instrumentSansRegular,
        fontSize: 13,
        lineHeight: 18,
        color: '#666666',
    },
    conditionLink: {
        textDecorationLine: 'underline',
    },
    conditionTextCrossedOut: {
        textDecorationLine: 'line-through',
        opacity: 0.5,
    },
    seeAllConditionsButton: {
        margin: 16,
        marginTop: 8,
        borderWidth: 1,
        borderColor: color.black,
        borderRadius: 96,
        height: 40,
        alignItems: 'center',
        justifyContent: 'center',
    },
    seeAllConditionsText: {
        fontFamily: font.instrumentSansSemiBold,
        fontSize: 14,
        lineHeight: 20,
        color: color.black,
    },

    giftCard: {
        borderRadius: 16,
        paddingHorizontal: 16,
        paddingTop: 16,
        paddingBottom: 16,
        width: '100%',
        minHeight: 230,
        justifyContent: 'space-between',
    },
    giftCardTop: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    giftCardBrandLogo: {
        height: BRAND_LOGO_HEIGHT,
        resizeMode: 'contain',
    },
    giftCardBrandName: {
        fontFamily: font.instrumentSansBold,
        fontSize: 14,
        lineHeight: 17,
    },
    giftCardBadge: {
        backgroundColor: 'rgba(255,255,255,0.2)',
        borderRadius: 12,
        paddingHorizontal: 8,
        paddingVertical: 4,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    giftCardBadgeText: {
        fontFamily: font.instrumentSansBold,
        fontSize: 14,
        lineHeight: 17,
        color: color.white,
    },
    giftCardCenter: {
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
    },
    giftCardAmount: {
        fontFamily: font.wallopSansBold,
        fontSize: 48,
        lineHeight: 58,
        letterSpacing: -0.96,
        textAlign: 'center',
    },
    cashbackBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        marginTop: 4,
    },
    cashbackBadgeAmount: {
        fontFamily: font.instrumentSansBold,
        fontSize: 14,
        lineHeight: 17,
        color: color.white,
        backgroundColor: 'rgba(255,255,255,0.25)',
        borderRadius: 6,
        paddingHorizontal: 6,
        paddingVertical: 2,
        overflow: 'hidden',
    },
    cashbackBadgeLabel: {
        fontFamily: font.instrumentSansRegular,
        fontSize: 16,
        lineHeight: 19,
        color: color.white,
    },

    orderCard: {
        backgroundColor: color.white,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: '#e3e3e3',
        padding: 16,
    },
    orderTitle: {
        fontFamily: font.instrumentSansBold,
        fontSize: 16,
        lineHeight: 19,
        color: color.black,
        marginBottom: 12,
    },
    orderRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 8,
    },
    orderRowBorderTop: {
        borderTopWidth: 1,
        borderTopColor: '#e3e3e3',
        marginTop: 8,
    },
    orderTitleBorderTop: {
        borderTopWidth: 1,
        borderTopColor: '#e3e3e3',
        paddingTop: 16,
        marginTop: 8,
    },
    orderItemLabel: {
        fontFamily: font.instrumentSansRegular,
        fontSize: 14,
        lineHeight: 20,
        color: color.black,
    },
    orderItemQty: {
        fontFamily: font.instrumentSansRegular,
        fontSize: 14,
        lineHeight: 20,
        color: color.black,
    },
    orderTotalLabel: {
        fontFamily: font.instrumentSansBold,
        fontSize: 14,
        lineHeight: 20,
        color: color.black,
    },
    orderTotalValue: {
        fontFamily: font.instrumentSansBold,
        fontSize: 14,
        lineHeight: 20,
        color: color.black,
    },
    cashbackDescription: {
        fontFamily: font.instrumentSansRegular,
        fontSize: 12,
        lineHeight: 17,
        color: color.doveGray,
        marginBottom: 4,
    },
});
